import React from 'react';
import { Check } from 'lucide-react';
import {
  annualSavingsPercent,
  annualMonthlyEquivalent,
  formatUsd,
  stripWholeDollarCents,
  trialEndDate,
  type TrialDescriptor,
} from '@/lib/paidLaunch';
import type { NativePackage } from '../../../services/purchasesService';

/**
 * Pricing selector for the trial screen (screen 5), which doubles as the
 * paywall: two light cards on the dark carousel — Monthly (led) and Yearly
 * (saving badge + effective monthly) — with a foliage check on the choice.
 * The host wires the selection to the real purchase (ValueOnboardingEntry).
 *
 * Price sources, in order:
 * - `nativePricing` (the RC offering's packages) when the host has loaded it —
 *   the store's OWN localized price strings and amounts, so what's shown always
 *   matches what the store sheet charges (any currency, any price change).
 * - The `monthlyPrice`/`annualPrice` USD amounts as fallback — web, and the
 *   beat before the offering loads on native.
 *
 * The TRIAL is a prop, never a constant: this screen is pre-login, so the only
 * thing that knows whether THIS person gets a free month is the store. Play
 * filters `subscriptionOfferDetails` by the signed-in Play account before we
 * ever see them, so the package's own free phase is already person-accurate —
 * the host derives it (see ValueOnboarding) and passes null when there isn't
 * one. Hardcoding it here promised a free month to every returning Play account
 * and then charged them on the spot.
 */

export type TrialPlan = 'annual' | 'monthly';

interface TrialPricingBoxProps {
  plan: TrialPlan;
  onPlan: (plan: TrialPlan) => void;
  monthlyPrice: number;
  annualPrice: number;
  /** Store packages (native, once loaded) — localized display + amounts win. */
  nativePricing?: { monthly: NativePackage | null; annual: NativePackage | null };
  /**
   * The free trial this person actually gets on the SELECTED plan, or null for
   * none — store-derived on native, asserted on web (copy policy on
   * TrialDescriptor). Null renders price-only terms with no trial language.
   */
  trial: TrialDescriptor | null;
}

/** Format `n` in the package's store currency; falls back to USD-style `formatUsd`. */
const moneyIn = (n: number, currencyCode?: string): string => {
  if (!currencyCode) return formatUsd(n);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return formatUsd(n);
  }
};

const PlanCard: React.FC<{
  title: string;
  price: string;
  sub: string;
  badge?: string;
  selected: boolean;
  onSelect: () => void;
}> = ({ title, price, sub, badge, selected, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    className={`relative rounded-2xl border-2 bg-cloud p-3 text-left shadow-md transition-colors ${
      selected ? 'border-foliage' : 'border-transparent opacity-90 hover:opacity-100'
    }`}
  >
    {badge && (
      <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-foliage px-2 py-0.5 text-[11px] font-semibold text-cloud">
        {badge}
      </span>
    )}
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-growth">{title}</div>
        <div className="mt-0.5 text-[15px] font-semibold text-growth">{price}</div>
        <div className="mt-0.5 text-[11px] text-growth/55">{sub}</div>
      </div>
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
          selected ? 'border-foliage bg-foliage' : 'border-mist bg-transparent'
        }`}
      >
        {selected && <Check className="h-3 w-3 text-cloud" />}
      </span>
    </div>
  </button>
);

export const TrialPricingBox: React.FC<TrialPricingBoxProps> = ({
  plan,
  onPlan,
  monthlyPrice,
  annualPrice,
  nativePricing,
  trial,
}) => {
  // Store packages win over the passed-in USD fallbacks — display AND math
  // come from the same source so nothing on this screen can contradict the
  // store's payment sheet.
  const monthlyPkg = nativePricing?.monthly ?? null;
  const annualPkg = nativePricing?.annual ?? null;
  const monthlyAmount = monthlyPkg?.price ?? monthlyPrice;
  const annualAmount = annualPkg?.price ?? annualPrice;
  const currency = annualPkg?.currencyCode ?? monthlyPkg?.currencyCode;

  // Whole-dollar prices drop the cents ("$15", never "$15.00") — store strings
  // are normalized at mapPackage, but strip again here so hosts passing raw
  // strings (previews, tests) can't regress the copy policy.
  const monthlyLabel = monthlyPkg?.priceString
    ? stripWholeDollarCents(monthlyPkg.priceString)
    : formatUsd(monthlyPrice);
  const annualLabel = annualPkg?.priceString
    ? stripWholeDollarCents(annualPkg.priceString)
    : formatUsd(annualPrice);

  const savePct = annualSavingsPercent(monthlyAmount, annualAmount);
  const perMonth = annualMonthlyEquivalent(annualAmount);

  // Only offer a plan the store can actually sell.
  //
  // `nativePricing` present means the store has answered, and a null package
  // there means that plan does not exist for this account/build. The price
  // fallbacks above are for WEB (Stripe bills from our own prices) — on native
  // they were rendering a Monthly card at the hardcoded $15 for a package Play
  // could not sell, so tapping it did nothing at all (Justin, Android device,
  // 2026-09-10). Never advertise a plan the next tap cannot fulfil.
  const storeAnswered = !!nativePricing;
  const monthlyOffered = !storeAnswered || !!monthlyPkg;
  const annualOffered = !storeAnswered || !!annualPkg;
  const bothOffered = monthlyOffered && annualOffered;

  // Honest terms for the selected plan: what's charged, and when. With no trial
  // there is no "no payment due today" to promise — say what's charged now.
  const priceTerm = plan === 'annual' ? `${annualLabel}/yr` : `${monthlyLabel}/mo`;
  const end = trial ? trialEndDate(new Date(), trial) : null;
  const billDate = end ? end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  return (
    <div>
      {/* Reassurance line above the plan cards — trial versions only, since it
          leads on "no payment now" and that is exactly what a trial means.
          Which half follows depends on the plan in front of them, so both
          claims are true where they appear: the annual saving is only real on
          annual, and asserting it while Monthly is selected would be the kind
          of bait we don't do. */}
      {trial && (
        <p className="mb-3 flex items-center justify-center gap-1.5 text-[13px] font-medium text-cloud">
          <Check className="h-3.5 w-3.5 shrink-0 text-foliage" strokeWidth={3} aria-hidden />
          {plan === 'annual' && savePct
            ? `No payment now. Save ${savePct}%`
            : 'No payment now. Cancel anytime.'}
        </p>
      )}

      <div
        className={`grid gap-3 ${bothOffered ? 'grid-cols-2' : 'grid-cols-1'}`}
        role="radiogroup"
        aria-label="Billing period"
      >
        {monthlyOffered && (
          <PlanCard
            title="Monthly"
            price={`${monthlyLabel}/mo`}
            sub={`Billed at ${monthlyLabel}/mo`}
            selected={plan === 'monthly'}
            onSelect={() => onPlan('monthly')}
          />
        )}
        {annualOffered && (
          <PlanCard
            title="Yearly"
            price={`${annualLabel}/yr`}
            sub={perMonth ? `Only ${moneyIn(Number(perMonth.toFixed(2)), currency)}/mo` : ''}
            badge={savePct && bothOffered ? `${savePct}% OFF` : undefined}
            selected={plan === 'annual'}
            onSelect={() => onPlan('annual')}
          />
        )}
      </div>

      <p className="mt-3 text-center text-[11px] leading-relaxed text-sand/60">
        {trial
          ? `No payment due today. Free for ${trial.periodLabel}, then ${priceTerm}${billDate ? ` on ${billDate}` : ''}. Membership renews until you cancel.`
          : `${priceTerm}, charged today. Membership renews until you cancel.`}
      </p>
    </div>
  );
};
